import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue,
} from '../../../../foundation/altzero_base/base/components/ui/select';
import { stepsFor, STEP_LABELS, type BillWorkflowStatus, type WorkflowStep } from '../../lib/billWorkflow';
import type { ReportBucket } from '../../lib/reportStatus';

// The person-set steps of a bill's status (lib/billWorkflow): offered only
// where a person decides — on a mismatch, internal or system review on a bill
// ready to send, and after a bill on the mismatch path is sent. Renders nothing elsewhere.

const CLEAR = '__clear__';

/** The review holds shown as compact buttons on a Ready to Send row. */
const FLAG_BUTTONS: Partial<Record<WorkflowStep, { label: string; aria: string; testId: string }>> = {
  internal_review: { label: 'Internal review', aria: 'Flag this bill for internal review', testId: 'bill-flag-review' },
  system_review: { label: 'System review', aria: 'Flag this bill for system review', testId: 'bill-flag-system-review' },
};

type WorkflowStepPickerProps = {
  status: BillWorkflowStatus;
  step: WorkflowStep | null;
  onChange: (step: WorkflowStep | null) => void;
  disabled?: boolean;
  /** The report's verdict bucket (BoardRow.bucket): narrows what internal review offers. */
  bucket?: ReportBucket | null;
};

export const WorkflowStepPicker = ({ status, step, onChange, disabled, bucket = null }: WorkflowStepPickerProps) => {
  const options = stepsFor(status, bucket);
  if (options.length === 0 && !step) return null;

  // Ready to Send offers only the two review holds and nothing to clear: a
  // Select on every such row is noise, so they are two small buttons.
  if (!step && options.length > 0 && options.every((o) => FLAG_BUTTONS[o])) {
    return (
      <div className="flex gap-1 mt-1">
        {options.map((o) => {
          const b = FLAG_BUTTONS[o]!;
          return (
            <Button
              key={o}
              type="button"
              variant="ghost"
              size="sm"
              data-testid={b.testId}
              aria-label={b.aria}
              disabled={disabled}
              onClick={() => onChange(o)}
              className="h-6 px-2 text-xs text-gray-500"
            >
              {b.label}
            </Button>
          );
        })}
      </div>
    );
  }

  return (
    <Select
      value=""
      disabled={disabled}
      onValueChange={(v) => onChange(v === CLEAR ? null : (v as WorkflowStep))}
    >
      <SelectTrigger data-testid="bill-workflow-step" aria-label="Set status for this bill" className="h-7 w-44 text-xs mt-1.5">
        <SelectValue placeholder="Set status…" />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o} value={o} className="text-xs">{STEP_LABELS[o]}</SelectItem>
        ))}
        {step && (
          <>
            {options.length > 0 && <SelectSeparator />}
            <SelectItem value={CLEAR} className="text-xs">Clear “{STEP_LABELS[step]}”</SelectItem>
          </>
        )}
      </SelectContent>
    </Select>
  );
};
